import { Flex, Typography } from '@iheartjane/reefer';
import type { Nullish } from '@iheartjane/types';
import { formatCurrency, pluralize } from '@iheartjane/utils';

import { useKioskSpacing } from '@jane/kiosk/hooks';
import { cartSize } from '@jane/shared-ecomm/util';
import type { PosCart } from '@jane/shared/types';

import { LineItem } from '../LineItem/LineItem';

interface CartTotalsCart {
  all_discounts_total?: number;
  item_total?: number;
  order_total?: number;
  products?: ReadonlyArray<{ count: number }>;
  rounding_amount?: number;
  sales_tax_amount?: number;
  service_fee_amount?: number;
  store_tax_amount?: number;
}

export interface CartTotalsProps {
  cart: CartTotalsCart;
  isLoading: boolean;
  posCart?: Nullish<PosCart>;
}

const calculateTotals = (
  cart: CartTotalsCart,
  posCart?: Nullish<PosCart>
): { cartTotal?: number; roundingAmount?: number; totalTaxAmount?: number } => {
  if (!cart || !cart.order_total) {
    return {
      cartTotal: undefined,
      roundingAmount: undefined,
      totalTaxAmount: undefined,
    };
  }

  const posCartTotals = posCart?.totals;

  const totalTaxAmount = posCartTotals
    ? (posCartTotals.sales_tax || 0) + (posCartTotals.store_tax || 0)
    : (cart.sales_tax_amount || 0) + (cart.store_tax_amount || 0);

  const cartTotal = posCart?.totals?.total || cart.order_total || 0;

  const roundingAmount = posCartTotals
    ? posCartTotals?.rounding_amount
    : cart.rounding_amount;

  return {
    cartTotal: cartTotal,
    roundingAmount: roundingAmount ?? undefined,
    totalTaxAmount: totalTaxAmount,
  };
};

export const CartTotals = ({ cart, isLoading, posCart }: CartTotalsProps) => {
  const { all_discounts_total, item_total, service_fee_amount } = cart;

  const { cartTotal, totalTaxAmount, roundingAmount } = calculateTotals(
    cart,
    posCart
  );

  const { getKioskSpacing, isIpad } = useKioskSpacing();

  return (
    <Flex
      flexDirection="column"
      grow={1}
      justifyContent="flex-end"
      data-testid="cart-totals"
    >
      <LineItem
        amount={item_total}
        isLoading={isLoading}
        label={`Subtotal (${pluralize({
          noun: 'item',
          number: cartSize(cart),
        })})`}
      />
      {all_discounts_total && all_discounts_total > 0 ? (
        <LineItem
          isLoading={isLoading}
          amount={
            <Typography color="sunset" variant="body-semibold" branded>
              {formatCurrency(-1 * all_discounts_total)}
            </Typography>
          }
          label="Total savings"
        />
      ) : null}
      {service_fee_amount && service_fee_amount > 0 ? (
        <LineItem
          isLoading={isLoading}
          amount={service_fee_amount}
          label="Service fee"
        />
      ) : null}
      <LineItem
        isLoading={isLoading}
        amount={totalTaxAmount}
        label="Estimated taxes"
      />
      {roundingAmount != 0 ? (
        <LineItem
          isLoading={isLoading}
          amount={roundingAmount}
          label="Rounding"
        />
      ) : null}

      <LineItem
        isLoading={isLoading}
        amount={
          <Typography variant={isIpad ? 'h3-semibold' : 'h2-semibold'}>
            {formatCurrency(cartTotal)}
          </Typography>
        }
        label={
          <Typography variant={isIpad ? 'h3-semibold' : 'h2-semibold'}>
            Estimated total
          </Typography>
        }
        mt={getKioskSpacing(24, 20)}
        mb={0}
      />
    </Flex>
  );
};
